import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Upload, Loader2, GripVertical, Star, X } from "lucide-react";
import { api, fileUrl, CONDITIONS, SPARE_CONDITIONS, PRICE_TYPES } from "@/lib/api";
import { RichTextEditor } from "@/components/RichTextEditor";
import { QuickAddSeller } from "@/components/admin/QuickAddSeller";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  name: "", category_id: "", brand: "", model: "", year: "", condition: "Good", price: "",
  price_type: "Negotiable", state: "", city: "", location: "", description: "",
  specs: [], images: [], videos: ["", ""], seller_id: "", status: "available", featured: false,
  listing_type: "used",
  part_number: "", compatible_machine: "", compatible_brand: "", warranty: "", availability: "",
  listing_fee: { amount: "", status: "Pending", payment_date: "", notes: "" },
};

export default function AdminMachineForm() {
  const { id } = useParams();
  const editing = id && id !== "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sellerOpen, setSellerOpen] = useState(false);
  const onSellerCreated = (s) => {
    setSellers((list) => [s, ...list]);
    setForm((f) => ({ ...f, seller_id: s.id }));
    setSellerOpen(false);
  };

  useEffect(() => {
    api.get("/categories?all=true").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/sellers").then((r) => setSellers(r.data)).catch(() => {});
    if (editing) {
      api.get(`/admin/machines/${id}`).then((r) => {
        const d = r.data;
        setForm({
          ...EMPTY, ...d,
          price: d.price || "",
          videos: [d.videos?.[0] || "", d.videos?.[1] || ""],
          listing_fee: { ...EMPTY.listing_fee, ...(d.listing_fee || {}) },
        });
      }).catch(() => toast.error("Failed to load machine"));
    }
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFee = (k, v) => setForm((f) => ({ ...f, listing_fee: { ...f.listing_fee, [k]: v } }));
  const isSpare = form.listing_type === "spare";
  const isNew = form.listing_type === "new";
  const catType = isSpare ? "spare" : "machine";
  const catOptions = categories.filter((c) => (c.type || "machine") === catType);
  const conditionOptions = isSpare ? SPARE_CONDITIONS : CONDITIONS;

  const addSpec = () => set("specs", [...form.specs, { name: "", value: "" }]);
  const updateSpec = (i, k, v) => set("specs", form.specs.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeSpec = (i) => set("specs", form.specs.filter((_, idx) => idx !== i));

  const uploadImages = async (files) => {
    setUploading(true);
    const newImages = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newImages.push({ id: crypto.randomUUID(), url: data.url, path: data.path });
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    set("images", [...form.images, ...newImages]);
    setUploading(false);
  };

  const removeImage = (idx) => set("images", form.images.filter((_, i) => i !== idx));
  const makeMain = (idx) => {
    const imgs = [...form.images];
    const [img] = imgs.splice(idx, 1);
    set("images", [img, ...imgs]);
  };
  const move = (idx, dir) => {
    const imgs = [...form.images];
    const j = idx + dir;
    if (j < 0 || j >= imgs.length) return;
    [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
    set("images", imgs);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Machine name is required");
    if (!form.category_id) return toast.error("Please select a category");
    if (!form.seller_id) return toast.error("Please select a seller");
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      videos: form.videos.filter((v) => v && v.trim()),
      specs: form.specs.filter((s) => s.name && s.value),
      listing_fee: { ...form.listing_fee, amount: parseFloat(form.listing_fee.amount) || 0 },
    };
    try {
      if (editing) await api.put(`/admin/machines/${id}`, payload);
      else {
        const { data } = await api.post("/admin/machines", payload);
        if (data.buyer_match_count > 0) toast.success(`${data.buyer_match_count} matching buyer requirement(s) found!`);
      }
      toast.success(editing ? "Machine updated" : "Machine published");
      navigate("/admin/machines");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/admin/machines" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ArrowLeft size={16} /> Back to Machines
      </Link>
      <h1 className="font-display text-2xl font-extrabold text-slate-900">{editing ? "Edit Listing" : "Add Listing"}</h1>

      <div className="mt-6 space-y-6">
        {/* Listing Type first */}
        <Section title="Listing Type">
          <Field label="What are you listing? *">
            <Select value={form.listing_type} onValueChange={(v) => set("listing_type", v)}>
              <SelectTrigger data-testid="mf-listing-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New Machine</SelectItem>
                <SelectItem value="used">Used Machine</SelectItem>
                <SelectItem value="spare">Spare Part</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </Section>

        {/* Details */}
        <Section title={isSpare ? "Spare Part Details" : "Machine Details"}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={isSpare ? "Part Name *" : "Machine Name *"}><Input data-testid="mf-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Category *">
              <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                <SelectTrigger data-testid="mf-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{catOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Brand"><Input data-testid="mf-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} /></Field>
            <Field label="Model"><Input data-testid="mf-model" value={form.model} onChange={(e) => set("model", e.target.value)} /></Field>
            {!isSpare && <Field label={isNew ? "Model Year" : "Manufacturing Year"}><Input data-testid="mf-year" value={form.year} onChange={(e) => set("year", e.target.value)} /></Field>}
            <Field label="Condition">
              <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                <SelectTrigger data-testid="mf-condition"><SelectValue /></SelectTrigger>
                <SelectContent>{conditionOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {isSpare && <Field label="Part Number"><Input data-testid="mf-part-number" value={form.part_number} onChange={(e) => set("part_number", e.target.value)} /></Field>}
            {isSpare && <Field label="Compatible Machine"><Input data-testid="mf-compatible-machine" value={form.compatible_machine} onChange={(e) => set("compatible_machine", e.target.value)} /></Field>}
            {isSpare && <Field label="Compatible Brand"><Input data-testid="mf-compatible-brand" value={form.compatible_brand} onChange={(e) => set("compatible_brand", e.target.value)} /></Field>}
            {isNew && <Field label="Warranty"><Input data-testid="mf-warranty" value={form.warranty} onChange={(e) => set("warranty", e.target.value)} placeholder="e.g. 1 Year" /></Field>}
            {isNew && <Field label="Availability"><Input data-testid="mf-availability" value={form.availability} onChange={(e) => set("availability", e.target.value)} placeholder="e.g. In Stock" /></Field>}
            <Field label="Price (₹)"><Input data-testid="mf-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} /></Field>
            <Field label="Price Type">
              <Select value={form.price_type} onValueChange={(v) => set("price_type", v)}>
                <SelectTrigger data-testid="mf-price-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PRICE_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="State"><Input data-testid="mf-state" value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="City"><Input data-testid="mf-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="Location (area/landmark)" full><Input data-testid="mf-location" value={form.location} onChange={(e) => set("location", e.target.value)} /></Field>
          </div>
        </Section>

        {/* Description */}
        <Section title="Description">
          <RichTextEditor testid="mf-description" value={form.description} onChange={(v) => set("description", v)} />
        </Section>

        {/* Specifications */}
        <Section title="Specifications">
          <div className="space-y-2">
            {form.specs.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`mf-spec-name-${i}`} placeholder="Specification" value={s.name} onChange={(e) => updateSpec(i, "name", e.target.value)} />
                <Input data-testid={`mf-spec-value-${i}`} placeholder="Value" value={s.value} onChange={(e) => updateSpec(i, "value", e.target.value)} />
                <button onClick={() => removeSpec(i)} className="rounded-lg border border-red-200 p-2 text-red-500"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <button data-testid="mf-add-spec" onClick={addSpec} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">
            <Plus size={15} /> ADD SPECIFICATION
          </button>
        </Section>

        {/* Photos */}
        <Section title="Photos">
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 py-8 text-sm font-semibold text-slate-500 hover:border-[hsl(var(--brand))]">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload size={18} />}
            {uploading ? "Uploading…" : "Click to upload photos (first photo is the main image)"}
            <input data-testid="mf-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImages(Array.from(e.target.files))} />
          </label>
          {form.images.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {form.images.map((img, i) => (
                <div key={img.id || i} className="group relative overflow-hidden rounded-lg border border-slate-200">
                  <img src={fileUrl(img.url)} alt="" className="aspect-square w-full object-cover" />
                  {i === 0 && <span className="absolute left-1.5 top-1.5 rounded bg-brand px-1.5 py-0.5 text-[10px] font-bold text-white">MAIN</span>}
                  <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                    {i !== 0 && <button onClick={() => makeMain(i)} title="Set main" className="rounded bg-white/90 p-1.5"><Star size={14} /></button>}
                    <button onClick={() => move(i, -1)} className="rounded bg-white/90 p-1.5 text-xs">◀</button>
                    <button onClick={() => move(i, 1)} className="rounded bg-white/90 p-1.5 text-xs">▶</button>
                    <button onClick={() => removeImage(i)} className="rounded bg-red-500 p-1.5 text-white"><X size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Videos */}
        <Section title="Videos (YouTube or external URL)">
          <div className="space-y-3">
            <Field label="Video 1 URL"><Input data-testid="mf-video1" value={form.videos[0]} onChange={(e) => set("videos", [e.target.value, form.videos[1]])} placeholder="https://youtube.com/watch?v=..." /></Field>
            <Field label="Video 2 URL"><Input data-testid="mf-video2" value={form.videos[1]} onChange={(e) => set("videos", [form.videos[0], e.target.value])} placeholder="Optional" /></Field>
          </div>
        </Section>

        {/* Seller */}
        <Section title="Seller">
          <Field label="Select Seller *">
            <Select value={form.seller_id} onValueChange={(v) => set("seller_id", v)}>
              <SelectTrigger data-testid="mf-seller"><SelectValue placeholder="Select seller" /></SelectTrigger>
              <SelectContent>{sellers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.company_name || s.city}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <button type="button" data-testid="mf-add-seller-btn" onClick={() => setSellerOpen(true)} className="mt-2 inline-block text-sm font-semibold text-[hsl(var(--brand))]">+ Add New Seller</button>
          <QuickAddSeller open={sellerOpen} onClose={() => setSellerOpen(false)} onCreated={onSellerCreated} />
        </Section>

        {/* Listing Controls */}
        <Section title="Listing Controls">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="mf-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="not_active">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Featured">
              <Select value={form.featured ? "yes" : "no"} onValueChange={(v) => set("featured", v === "yes")}>
                <SelectTrigger data-testid="mf-featured"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        {/* Listing Fee */}
        <Section title="Listing Fee (Admin only)">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Fee Amount (₹)"><Input data-testid="mf-fee-amount" type="number" value={form.listing_fee.amount} onChange={(e) => setFee("amount", e.target.value)} /></Field>
            <Field label="Fee Status">
              <Select value={form.listing_fee.status} onValueChange={(v) => setFee("status", v)}>
                <SelectTrigger data-testid="mf-fee-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Payment Date"><Input data-testid="mf-fee-date" type="date" value={form.listing_fee.payment_date} onChange={(e) => setFee("payment_date", e.target.value)} /></Field>
            <Field label="Payment Notes"><Input data-testid="mf-fee-notes" value={form.listing_fee.notes} onChange={(e) => setFee("notes", e.target.value)} /></Field>
          </div>
        </Section>

        <div className="sticky bottom-0 flex gap-3 border-t border-slate-200 bg-slate-50 py-4">
          <button data-testid="mf-save" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-brand px-8 py-3 font-bold text-white disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "SAVE CHANGES" : "SAVE & PUBLISH"}
          </button>
          <Link to="/admin/machines" className="rounded-full border border-slate-300 px-6 py-3 font-bold text-slate-700">Cancel</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-4 font-display font-bold text-slate-900">{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
